import {getLogger} from './logging'
import {
  pushDataLayerEvent,
  getOrCreateVisitorData,
  getCurrentPageInfo
} from './analyticsHelpers'

const logger = getLogger('funnelAnalytics')

type JourneyStep = 'account_created' | 'first_login' | 'book_now_clicked'
type JourneyStatus = 'in_progress' | 'completed' | 'abandoned'

interface FirstTimeUserJourneyData {
  userId: string
  sessionId: string
  deviceType: string
  startTime: number
  accountCreated?: string // timestamp
  firstLoginCompleted?: string // timestamp
  bookNowClicked?: string // timestamp
  lastUpdated: string
}

const FIRST_TIME_USER_JOURNEY_KEY = 'guest_portal_first_time_user_journey'

/**
 * Gets current first-time user journey data
 */
function getFirstTimeUserJourneyData(): FirstTimeUserJourneyData | null {
  try {
    const data = sessionStorage.getItem(FIRST_TIME_USER_JOURNEY_KEY)
    return data ? JSON.parse(data) : null
  } catch (error) {
    logger.error({
      message: 'Error getting first-time user journey data',
      data: {error}
    })
    return null
  }
}

/**
 * Updates first-time user journey data
 */
function updateFirstTimeUserJourneyData(
  step: JourneyStep,
  userId: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()
    const deviceType = getDeviceType()
    const timestamp = new Date().toISOString()

    // Get existing journey or create new one
    let journeyData = getFirstTimeUserJourneyData()

    if (!journeyData) {
      // No existing journey - create fresh journey
      journeyData = {
        userId,
        sessionId: visitorData.sessionId,
        deviceType,
        startTime: Date.now(),
        lastUpdated: timestamp
      }
    } else if (journeyData.sessionId !== visitorData.sessionId) {
      // Update session ID but keep existing journey data - this handles login session changes
      journeyData.sessionId = visitorData.sessionId
      journeyData.userId = userId // Make sure userId matches
    }

    if (step === 'account_created') {
      journeyData.accountCreated = timestamp
    } else if (step === 'first_login') {
      journeyData.firstLoginCompleted = timestamp
    } else if (step === 'book_now_clicked') {
      journeyData.bookNowClicked = timestamp
    }

    journeyData.lastUpdated = timestamp
    journeyData.userId = userId // Update userId in case it changed

    sessionStorage.setItem(
      FIRST_TIME_USER_JOURNEY_KEY,
      JSON.stringify(journeyData)
    )

    // Check if journey is complete
    checkFirstTimeUserJourneyCompletion(journeyData)
  } catch (error) {
    logger.error({
      message: 'Error updating first-time user journey data',
      data: {error, step, userId}
    })
  }
}

/**
 * Checks if the first-time user journey is complete and sends appropriate events
 */
function checkFirstTimeUserJourneyCompletion(
  journeyData: FirstTimeUserJourneyData
): void {
  const isComplete =
    journeyData.accountCreated &&
    journeyData.firstLoginCompleted &&
    journeyData.bookNowClicked

  if (isComplete) {
    sendFirstTimeUserJourneyEvent(journeyData, 'completed')
    clearFirstTimeUserJourney()
  }
}

/**
 * Analyzes first-time user journey and sends abandonment event if needed
 * Now includes logic to avoid false abandonment during email verification
 */
function analyzeFirstTimeUserJourneyOnExit(
  exitType?: 'tab_switch' | 'page_unload' | 'timeout'
): void {
  try {
    const journeyData = getFirstTimeUserJourneyData()
    if (!journeyData) {
      return
    }

    const hasAccountCreated = !!journeyData.accountCreated
    const hasFirstLogin = !!journeyData.firstLoginCompleted
    const hasBookNowClicked = !!journeyData.bookNowClicked
    const currentPage = getCurrentPageInfo().pathname

    // SPECIAL CASE: Don't analyze abandonment on tab switches during verification
    if (exitType === 'tab_switch') {
      // If user is on verification/confirmation pages, don't treat tab switch as abandonment
      const isOnVerificationPage =
        currentPage === '/confirm' || currentPage === '/login'

      if (isOnVerificationPage && hasAccountCreated && !hasFirstLogin) {
        return // Don't analyze for abandonment during email verification
      }
    }

    // Check if this is a true abandonment or just navigation in progress
    if (hasAccountCreated && !hasFirstLogin) {
      // Only abandon if it's a page unload (not tab switch during verification)
      if (exitType === 'page_unload' || exitType === 'timeout') {
        sendFirstTimeUserJourneyEvent(
          journeyData,
          'abandoned',
          'account_created'
        )
        clearFirstTimeUserJourney() // Clear when user truly abandoned
      }
    } else if (hasAccountCreated && hasFirstLogin && !hasBookNowClicked) {
      // User completed account + login but no book now yet
      // Check if journey has been inactive for too long (e.g., 30 minutes)
      const journeyAge = Date.now() - journeyData.startTime
      const maxJourneyTime = 30 * 60 * 1000 // 30 minutes

      if (journeyAge > maxJourneyTime || exitType === 'timeout') {
        sendFirstTimeUserJourneyEvent(journeyData, 'abandoned', 'first_login')
        clearFirstTimeUserJourney()
      } else {
        // Journey is still active - keep the data
      }
    }

    // Note: We don't automatically clear for in-progress journeys anymore
  } catch (error) {
    logger.error({
      message: 'Error analyzing first-time user journey on exit',
      data: {error}
    })
  }
}

/**
 * Sends the unified first-time user journey event
 */
function sendFirstTimeUserJourneyEvent(
  journeyData: FirstTimeUserJourneyData,
  status: JourneyStatus,
  abandonmentPoint?: JourneyStep
): void {
  try {
    const sessionDuration = (Date.now() - journeyData.startTime) / 1000 / 60 // minutes
    const completedSteps: string[] = []

    if (journeyData.accountCreated) {
      completedSteps.push('account_created')
    }
    if (journeyData.firstLoginCompleted) {
      completedSteps.push('first_login')
    }
    if (journeyData.bookNowClicked) {
      completedSteps.push('book_now_clicked')
    }

    const event = {
      event: 'guest_portal_first_time_user_journey',
      journey_status: status,
      completed_steps: completedSteps,
      abandonment_point: abandonmentPoint || null,
      session_duration_minutes: Math.round(sessionDuration * 100) / 100,
      device_type: journeyData.deviceType,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: journeyData.sessionId,
      user_id: journeyData.userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error sending first-time user journey event',
      data: {error, status, abandonmentPoint}
    })
  }
}

/**
 * Clears the first-time user journey data
 */
function clearFirstTimeUserJourney(): void {
  try {
    sessionStorage.removeItem(FIRST_TIME_USER_JOURNEY_KEY)
  } catch (error) {
    logger.error({
      message: 'Error clearing first-time user journey data',
      data: {error}
    })
  }
}

export function trackAccountCreated(
  userId?: string,
  hasExistingReservations?: boolean
): void {
  // Only track first-time user journey (no existing reservations)
  if (userId && !hasExistingReservations) {
    updateFirstTimeUserJourneyData('account_created', userId)
  }
}

export function trackFirstLogin(userId?: string): void {
  // Only track first-time user journey
  if (userId) {
    updateFirstTimeUserJourneyData('first_login', userId)
  }
}

export function trackBookNowClicked(userId?: string): void {
  // Only track first-time user journey
  if (userId) {
    updateFirstTimeUserJourneyData('book_now_clicked', userId)
  }
}

export function analyzeFirstTimeUserJourney(
  exitType?: 'tab_switch' | 'page_unload' | 'timeout'
): void {
  analyzeFirstTimeUserJourneyOnExit(exitType)
}

export function getFirstTimeUserJourneyStatus(): {
  isActive: boolean
  completedSteps: string[]
  userId?: string
  sessionDuration?: number
  journeyAge?: number
} {
  const journeyData = getFirstTimeUserJourneyData()
  if (!journeyData) {
    return {isActive: false, completedSteps: []}
  }

  const completedSteps: string[] = []
  if (journeyData.accountCreated) {
    completedSteps.push('account_created')
  }
  if (journeyData.firstLoginCompleted) {
    completedSteps.push('first_login')
  }
  if (journeyData.bookNowClicked) {
    completedSteps.push('book_now_clicked')
  }

  const sessionDuration = (Date.now() - journeyData.startTime) / 1000 / 60
  const journeyAge = (Date.now() - journeyData.startTime) / 1000 / 60 // Also in minutes

  return {
    isActive: true,
    completedSteps,
    userId: journeyData.userId,
    sessionDuration: Math.round(sessionDuration * 100) / 100,
    journeyAge: Math.round((journeyAge * 100) / 100)
  }
}

export function detectAndTrackBookNow(
  element: HTMLElement,
  destinationUrl?: string,
  userId?: string
): boolean {
  try {
    const text = element.textContent?.toLowerCase() || ''
    const href = destinationUrl?.toLowerCase() || ''

    // More precise book now detection
    const textMatches = {
      'book now': text.includes('book now')
    }

    const textMatch = Object.values(textMatches).some(match => match)
    const isBookNow = textMatch

    if (isBookNow) {
      // Check if user is in a first-time journey
      const journeyStatus = getFirstTimeUserJourneyStatus()

      if (
        journeyStatus.isActive &&
        journeyStatus.userId &&
        journeyStatus.completedSteps.includes('first_login')
      ) {
        trackBookNowClicked(journeyStatus.userId)
        return true
      } else if (userId) {
        // Fallback: if userId provided directly, try to mark it
        trackBookNowClicked(userId)
        return true
      }
    }

    return false
  } catch (error) {
    logger.error({
      message: 'Error in book now detection',
      data: {error}
    })
    return false
  }
}

export function getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  const width = window.innerWidth
  const userAgent = navigator.userAgent.toLowerCase()

  // Check for mobile user agent
  if (/mobile|android|iphone|ipad|phone/i.test(userAgent)) {
    if (width <= 768) {
      return 'mobile'
    } else if (width <= 1024) {
      return 'tablet'
    }
  }

  // Check by screen size
  if (width <= 768) {
    return 'mobile'
  } else if (width <= 1024) {
    return 'tablet'
  } else {
    return 'desktop'
  }
}
